import { fraLagret, type Tekstdok } from "@infoflyt/shared/tekst";
import { supabase } from "./supabase";

/**
 * Beskjedfeltene: skriving fra nettflata.
 *
 * TRE MOTTAKERE, ikke tre kopier. En beskjed til teknisk crew om batterier
 * har ingenting i innholdssidens felt å gjøre, og den som leser skal kunne
 * hoppe rett til sitt.
 *
 * Innholdet lagres som et dokument (`jsonb`), ikke som HTML. Modellen står i
 * `@infoflyt/shared/tekst`, og grunnen står der: HTML fra et
 * `contenteditable` er noe appen måtte stolt på.
 */

export interface Felt {
  readonly slug: string;
  readonly tittel: string;
}

/*
 * Rekkefølgen er lesefølgen, og den er ikke tilfeldig: teknisk først fordi
 * det er dem beskjedene oftest haster for.
 */
export const FELTER: readonly Felt[] = [
  { slug: "brief-teknisk", tittel: "Teknisk crew" },
  { slug: "brief-innhold", tittel: "Innhold" },
  { slug: "brief-prod", tittel: "Prod" },
];


export interface Tekstflate {
  readonly slug: string;
  readonly tittel: string;
  readonly dok: Tekstdok;
  readonly endretIso: string | null;
}

export async function hentAlle(produksjonId: string): Promise<Tekstflate[]> {
  if (supabase === null) return [];
  const { data, error } = await supabase
    .from("text_page")
    .select("slug, title, content, updated_at")
    .eq("production_id", produksjonId);
  if (error !== null) return [];
  return (data ?? []).map((r) => ({
    slug: String(r.slug),
    tittel: String(r.title),
    dok: fraLagret(r.content),
    endretIso: String(r.updated_at),
  }));
}

/**
 * Lagrer ett felt. Oppretter raden om den ikke finnes.
 *
 * `upsert` på (production_id, slug): feltet skal kunne skrives i fra første
 * tegn uten at noen har «opprettet» noe først.
 */
export async function lagre(
  produksjonId: string, slug: string, tittel: string, dok: Tekstdok,
): Promise<string | null> {
  if (supabase === null) return null;
  const naa = new Date().toISOString();
  const { data, error } = await supabase
    .from("text_page")
    .upsert(
      { production_id: produksjonId, slug, title: tittel, content: dok, updated_at: naa },
      { onConflict: "production_id,slug" },
    )
    .select("updated_at")
    .single();
  if (error !== null || data === null) return null;
  return String(data.updated_at);
}
